import QRCode from "qrcode";
import panzoom from "panzoom";
import { CONFIG, ELEMENT_IDS } from "../config";
import type { QRData } from "../types";

export class UIManager {
  private panzoomInstance: any = null;
  private closeButton: HTMLElement | null = null;
  private rotateButton: HTMLElement | null = null;
  private mirrorButton: HTMLElement | null = null;
  private snapshotButton: HTMLElement | null = null;
  private snapshotCanvas: HTMLCanvasElement | null = null;
  private snapshotImage: HTMLImageElement | null = null;
  private snapshotMode: boolean = false;
  private snapshotTransforms: { rotation: number; mirrored: boolean } = { rotation: 0, mirrored: false };
  private snapshotPanzoom: any = null;
  private videoRotation: number = 0;
  private videoMirrored: boolean = false;

  showScreen(screenId: string): void {
    document.querySelectorAll(".screen").forEach((screen) => {
      screen.classList.add("hidden");
    });
    document.getElementById(screenId)?.classList.remove("hidden");
  }

  showInstallScreen(): void {
    this.showScreen(ELEMENT_IDS.INSTALL_SCREEN);
  }

  showModeSelection(): void {
    this.showScreen(ELEMENT_IDS.MODE_SCREEN);
  }

  showViewerScreen(): void {
    this.showScreen(ELEMENT_IDS.VIEWER_SCREEN);
  }

  showCameraScreen(): void {
    this.showScreen(ELEMENT_IDS.CAMERA_SCREEN);
  }

  async generateQRCode(connectionId: string, serverUrl: string): Promise<void> {
    const qrCanvas = document.getElementById(ELEMENT_IDS.QR_CANVAS) as HTMLCanvasElement;
    if (qrCanvas) {
      const qrData: QRData = {
        connectionId,
        server: serverUrl,
      };

      await QRCode.toCanvas(qrCanvas, JSON.stringify(qrData), {
        width: CONFIG.QR.CANVAS_SIZE,
        margin: CONFIG.QR.CANVAS_MARGIN,
      });
    }
  }

  updateConnectionStatus(status: string): void {
    const statusEl = document.getElementById(ELEMENT_IDS.CONNECTION_STATUS);
    if (statusEl) {
      statusEl.textContent = status;
    }
  }

  updateCameraConnectionStatus(status: string): void {
    const statusEl = document.getElementById(ELEMENT_IDS.CAMERA_CONNECTION_STATUS);
    if (statusEl) {
      statusEl.textContent = status;
    }
  }

  enterFullscreenVideo(): void {
    const viewerSetup = document.getElementById(ELEMENT_IDS.VIEWER_SETUP);
    const fullscreenVideo = document.getElementById(ELEMENT_IDS.FULLSCREEN_VIDEO);

    if (viewerSetup && fullscreenVideo) {
      viewerSetup.classList.add("hidden");
      fullscreenVideo.classList.remove("hidden");

      // Create floating buttons
      this.createFullscreenButtons();

      // Show resolution indicator
      this.showViewerResolution();
    }
  }

  exitFullscreenVideo(): void {
    const viewerSetup = document.getElementById(ELEMENT_IDS.VIEWER_SETUP);
    const fullscreenVideo = document.getElementById(ELEMENT_IDS.FULLSCREEN_VIDEO);

    if (viewerSetup && fullscreenVideo) {
      fullscreenVideo.classList.add("hidden");
      viewerSetup.classList.remove("hidden");

      // Remove floating buttons and hide resolution indicator
      this.removeFullscreenButtons();
      this.hideViewerResolution();
    }
  }

  setupRemoteVideo(stream: MediaStream): void {
    const remoteVideo = document.getElementById(ELEMENT_IDS.REMOTE_VIDEO) as HTMLVideoElement;
    if (remoteVideo) {
      // Enable all video tracks
      stream.getTracks().forEach((track) => {
        if (track.kind === "video") {
          track.enabled = true;
        }
      });

      // Set up video element
      remoteVideo.srcObject = stream;
      remoteVideo.style.display = "block";
      remoteVideo.style.objectFit = "contain";
      remoteVideo.setAttribute("playsinline", "true");
      remoteVideo.setAttribute("autoplay", "true");
      remoteVideo.muted = false;

      // Start video playback
      remoteVideo.play().catch((error) => {
        // Silent error handling
      });

      // Switch to fullscreen and update status
      this.enterFullscreenVideo();
      this.updateConnectionStatus("Video connected!");

      // Initialize panzoom after DOM update
      setTimeout(() => {
        const videoContainer = document.getElementById(ELEMENT_IDS.VIDEO_CONTAINER);
        if (videoContainer) {
          this.initializePanzoom(videoContainer as HTMLElement);
        }
      }, 100);
    }
  }

  private createFullscreenButtons(): void {
    // Get HTML buttons
    this.closeButton = document.getElementById(ELEMENT_IDS.CLOSE_BTN) as HTMLElement;
    this.rotateButton = document.getElementById(ELEMENT_IDS.ROTATE_BTN) as HTMLElement;
    this.mirrorButton = document.getElementById(ELEMENT_IDS.MIRROR_BTN) as HTMLElement;
    this.snapshotButton = document.getElementById(ELEMENT_IDS.SNAPSHOT_BTN) as HTMLElement;

    // Show buttons
    this.closeButton?.classList.remove("hidden");
    this.rotateButton?.classList.remove("hidden");
    this.mirrorButton?.classList.remove("hidden");
    this.snapshotButton?.classList.remove("hidden");

    // Add event listeners
    this.closeButton?.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      // If in snapshot mode, exit it first
      if (this.snapshotMode) {
        this.exitSnapshotMode();
      }
      
      this.exitFullscreenVideo();
    });

    this.rotateButton?.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.rotateVideo();
    });

    this.mirrorButton?.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.mirrorVideo();
    });

    this.snapshotButton?.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.toggleSnapshotMode();
    });
  }

  private removeFullscreenButtons(): void {
    // Hide buttons instead of removing them
    this.closeButton?.classList.add("hidden");
    this.rotateButton?.classList.add("hidden");
    this.mirrorButton?.classList.add("hidden");
    this.snapshotButton?.classList.add("hidden");
    
    // Clear references
    this.closeButton = null;
    this.rotateButton = null;
    this.mirrorButton = null;
    this.snapshotButton = null;
  }

  private initializePanzoom(element: HTMLElement): void {
    // Clean up existing panzoom instance if any
    if (this.panzoomInstance) {
      this.panzoomInstance.dispose();
    }

    // Initialize panzoom with optimal settings
    this.panzoomInstance = panzoom(element, CONFIG.VIDEO.PANZOOM);
  }

  private rotateVideo(): void {
    // In split mode, only affect live video, not snapshot
    if (this.snapshotMode) {
      // Only rotate the live feed, snapshot keeps its original transform
      this.videoRotation = (this.videoRotation + CONFIG.VIDEO.ROTATION_STEP) % 360;
      this.applyLiveFeedTransforms();
    } else {
      // Normal fullscreen mode - rotate as before
      this.videoRotation = (this.videoRotation + CONFIG.VIDEO.ROTATION_STEP) % 360;

      const remoteVideo = document.getElementById(ELEMENT_IDS.REMOTE_VIDEO) as HTMLVideoElement;
      const videoContainer = document.getElementById(ELEMENT_IDS.VIDEO_CONTAINER);

      if (remoteVideo && videoContainer && this.panzoomInstance) {
        // Completely dispose of panzoom
        this.panzoomInstance.dispose();
        this.panzoomInstance = null;

        // Clear all transforms
        remoteVideo.style.transform = "";
        videoContainer.style.transform = "";

        // Apply rotation and mirror transforms
        this.applyVideoTransforms(remoteVideo);

        // Reinitialize panzoom with fresh state
        this.initializePanzoom(videoContainer as HTMLElement);
      }
    }
  }

  private mirrorVideo(): void {
    // Toggle mirror state
    this.videoMirrored = !this.videoMirrored;

    // In split mode, only affect live video, not snapshot
    if (this.snapshotMode) {
      // Only mirror the live feed, snapshot keeps its original transform
      this.applyLiveFeedTransforms();
    } else {
      // Normal fullscreen mode - mirror as before
      const remoteVideo = document.getElementById(ELEMENT_IDS.REMOTE_VIDEO) as HTMLVideoElement;
      const videoContainer = document.getElementById(ELEMENT_IDS.VIDEO_CONTAINER);

      if (remoteVideo && videoContainer && this.panzoomInstance) {
        // Completely dispose of panzoom
        this.panzoomInstance.dispose();
        this.panzoomInstance = null;

        // Clear all transforms
        remoteVideo.style.transform = "";
        videoContainer.style.transform = "";

        // Apply rotation and mirror transforms
        this.applyVideoTransforms(remoteVideo);

        // Reinitialize panzoom with fresh state
        this.initializePanzoom(videoContainer as HTMLElement);
      }
    }
  }

  private applyVideoTransforms(remoteVideo: HTMLVideoElement): void {
    const transforms = [];
    
    // Apply rotation
    if (this.videoRotation !== 0) {
      transforms.push(`rotate(${this.videoRotation}deg)`);
    }
    
    // Apply mirror (horizontal flip)
    if (this.videoMirrored) {
      transforms.push('scaleX(-1)');
    }
    
    remoteVideo.style.transform = transforms.join(' ');
  }

  private applyLiveFeedTransforms(): void {
    // Apply transforms only to live feed in split mode without reinitializing panzoom
    const remoteVideo = document.getElementById(ELEMENT_IDS.REMOTE_VIDEO) as HTMLVideoElement;
    if (remoteVideo) {
      this.applyVideoTransforms(remoteVideo);
    }
  }

  private toggleSnapshotMode(): void {
    if (this.snapshotMode) {
      this.exitSnapshotMode();
    } else {
      this.takeSnapshot();
    }
  }

  private takeSnapshot(): void {
    const remoteVideo = document.getElementById(ELEMENT_IDS.REMOTE_VIDEO) as HTMLVideoElement;
    if (!remoteVideo) return;

    // Create canvas if it doesn't exist
    if (!this.snapshotCanvas) {
      this.snapshotCanvas = document.createElement('canvas');
    }

    // Set canvas size to match video dimensions
    this.snapshotCanvas.width = remoteVideo.videoWidth;
    this.snapshotCanvas.height = remoteVideo.videoHeight;

    const ctx = this.snapshotCanvas.getContext('2d');
    if (!ctx) return;

    // Save current transforms
    this.snapshotTransforms = {
      rotation: this.videoRotation,
      mirrored: this.videoMirrored
    };

    // Apply transforms to canvas context
    ctx.save();
    ctx.translate(this.snapshotCanvas.width / 2, this.snapshotCanvas.height / 2);
    
    if (this.videoMirrored) {
      ctx.scale(-1, 1);
    }
    
    if (this.videoRotation !== 0) {
      ctx.rotate((this.videoRotation * Math.PI) / 180);
    }

    ctx.translate(-this.snapshotCanvas.width / 2, -this.snapshotCanvas.height / 2);

    // Draw video frame to canvas
    ctx.drawImage(remoteVideo, 0, 0, this.snapshotCanvas.width, this.snapshotCanvas.height);
    ctx.restore();

    // Convert canvas to image
    const dataUrl = this.snapshotCanvas.toDataURL('image/png');
    
    // Create or update snapshot image
    const snapshotImage = document.getElementById(ELEMENT_IDS.SNAPSHOT_IMAGE) as HTMLImageElement;
    if (snapshotImage) {
      snapshotImage.src = dataUrl;
      this.snapshotImage = snapshotImage;
    }

    // Enter snapshot mode
    this.enterSnapshotMode();
  }

  private enterSnapshotMode(): void {
    this.snapshotMode = true;
    
    // Update button appearance to indicate active state
    this.snapshotButton?.classList.add("active");

    this.createSplitScreenLayout();
  }

  private exitSnapshotMode(): void {
    this.snapshotMode = false;
    
    // Reset button appearance
    this.snapshotButton?.classList.remove("active");

    this.restoreFullscreenLayout();
  }

  private createSplitScreenLayout(): void {
    const fullscreenVideo = document.getElementById(ELEMENT_IDS.FULLSCREEN_VIDEO);
    const videoContainer = document.getElementById(ELEMENT_IDS.VIDEO_CONTAINER);
    const snapshotContainer = document.getElementById(ELEMENT_IDS.SNAPSHOT_CONTAINER);
    
    if (fullscreenVideo && videoContainer && snapshotContainer) {
      // Add split-screen class to enable CSS layout
      fullscreenVideo.classList.add('split-screen');
      
      // Show snapshot container
      snapshotContainer.classList.remove('hidden');
      
      // Initialize panzoom for snapshot
      const snapshotWrapper = document.getElementById(ELEMENT_IDS.SNAPSHOT_WRAPPER);
      if (snapshotWrapper) {
        this.initializeSnapshotPanzoom(snapshotWrapper);
      }
    }
  }

  private restoreFullscreenLayout(): void {
    const fullscreenVideo = document.getElementById(ELEMENT_IDS.FULLSCREEN_VIDEO);
    const snapshotContainer = document.getElementById(ELEMENT_IDS.SNAPSHOT_CONTAINER);
    const videoContainer = document.getElementById(ELEMENT_IDS.VIDEO_CONTAINER);
    const remoteVideo = document.getElementById(ELEMENT_IDS.REMOTE_VIDEO) as HTMLVideoElement;
    
    if (fullscreenVideo && snapshotContainer) {
      // Remove split-screen class
      fullscreenVideo.classList.remove('split-screen');
      
      // Hide snapshot container
      snapshotContainer.classList.add('hidden');
      
      // Clear snapshot image src to fully remove it
      const snapshotImage = document.getElementById(ELEMENT_IDS.SNAPSHOT_IMAGE) as HTMLImageElement;
      if (snapshotImage) {
        snapshotImage.src = '';
      }
      
      // Clean up snapshot panzoom
      if (this.snapshotPanzoom) {
        this.snapshotPanzoom.dispose();
        this.snapshotPanzoom = null;
      }
      
      // Reset video container to fullscreen layout
      if (videoContainer) {
        videoContainer.style.width = '';
        videoContainer.style.height = '';
      }
      
      // Reinitialize live video panzoom to ensure proper fullscreen state
      if (videoContainer && remoteVideo && this.panzoomInstance) {
        this.panzoomInstance.dispose();
        this.panzoomInstance = null;
        
        // Clear any split-screen transforms
        remoteVideo.style.transform = '';
        videoContainer.style.transform = '';
        
        // Reapply current transforms and reinitialize panzoom
        this.applyVideoTransforms(remoteVideo);
        this.initializePanzoom(videoContainer);
      }
    }
  }

  private initializeSnapshotPanzoom(element: HTMLElement): void {
    // Clean up existing snapshot panzoom instance if any
    if (this.snapshotPanzoom) {
      this.snapshotPanzoom.dispose();
    }

    // Initialize panzoom with same settings as video
    this.snapshotPanzoom = panzoom(element, CONFIG.VIDEO.PANZOOM);
  }

  cleanup(): void {
    // Clean up panzoom instances
    if (this.panzoomInstance) {
      this.panzoomInstance.dispose();
      this.panzoomInstance = null;
    }
    if (this.snapshotPanzoom) {
      this.snapshotPanzoom.dispose();
      this.snapshotPanzoom = null;
    }

    // Exit snapshot mode if active
    if (this.snapshotMode) {
      this.exitSnapshotMode();
    }

    // Remove any floating buttons
    this.removeFullscreenButtons();

    // Reset fullscreen state
    this.exitFullscreenVideo();

    // Reset video rotation, mirror, and snapshot state
    this.videoRotation = 0;
    this.videoMirrored = false;
    this.snapshotMode = false;
    this.snapshotCanvas = null;
    this.snapshotImage = null;

    // Hide stats overlay and viewer resolution
    this.hideVideoStats();
    this.hideViewerStats();
    this.hideViewerResolution();
  }

  showVideoStats(): void {
    const statsOverlay = document.getElementById(ELEMENT_IDS.VIDEO_STATS);
    if (statsOverlay) {
      statsOverlay.classList.remove("hidden");
    }
  }

  hideVideoStats(): void {
    const statsOverlay = document.getElementById(ELEMENT_IDS.VIDEO_STATS);
    if (statsOverlay) {
      statsOverlay.classList.add("hidden");
    }
  }

  updateVideoStats(resolution: { width: number; height: number }, bitrate: number, fps: number): void {
    const resolutionEl = document.getElementById(ELEMENT_IDS.STATS_RESOLUTION);
    const bitrateEl = document.getElementById(ELEMENT_IDS.STATS_BITRATE);
    const fpsEl = document.getElementById(ELEMENT_IDS.STATS_FPS);

    if (resolutionEl) resolutionEl.textContent = `${resolution.width}×${resolution.height}`;
    if (bitrateEl) bitrateEl.textContent = this.formatBitrate(bitrate);
    if (fpsEl) fpsEl.textContent = `${Math.round(fps)}`;
  }

  private formatBitrate(bitrate: number): string {
    if (bitrate >= 1000000) {
      return `${(bitrate / 1000000).toFixed(1)}M`;
    } else if (bitrate >= 1000) {
      return `${(bitrate / 1000).toFixed(0)}K`;
    }
    return `${bitrate}`;
  }

  // Viewer resolution indicator methods
  showViewerResolution(): void {
    const resolutionIndicator = document.getElementById(ELEMENT_IDS.VIEWER_RESOLUTION);
    if (resolutionIndicator) {
      resolutionIndicator.classList.remove("hidden");
    }
  }

  hideViewerResolution(): void {
    const resolutionIndicator = document.getElementById(ELEMENT_IDS.VIEWER_RESOLUTION);
    if (resolutionIndicator) {
      resolutionIndicator.classList.add("hidden");
    }
  }

  updateViewerResolution(width: number, height: number): void {
    const resolutionIndicator = document.getElementById(ELEMENT_IDS.VIEWER_RESOLUTION);
    if (resolutionIndicator) {
      resolutionIndicator.textContent = `${width}×${height}`;
    }
  }

  showViewerStats(): void {
    const statsOverlay = document.getElementById(ELEMENT_IDS.VIEWER_STATS);
    if (statsOverlay) {
      statsOverlay.classList.remove("hidden");
    }
  }

  hideViewerStats(): void {
    const statsOverlay = document.getElementById(ELEMENT_IDS.VIEWER_STATS);
    if (statsOverlay) {
      statsOverlay.classList.add("hidden");
    }
  }

  updateViewerStats(resolution: { width: number; height: number }, bitrate: number, fps: number): void {
    const resolutionEl = document.getElementById(ELEMENT_IDS.VIEWER_STATS_RESOLUTION);
    const bitrateEl = document.getElementById(ELEMENT_IDS.VIEWER_STATS_BITRATE);
    const fpsEl = document.getElementById(ELEMENT_IDS.VIEWER_STATS_FPS);

    if (resolutionEl) resolutionEl.textContent = `${resolution.width}×${resolution.height}`;
    if (bitrateEl) bitrateEl.textContent = this.formatBitrate(bitrate);
    if (fpsEl) fpsEl.textContent = `${Math.round(fps)}`;
  }
}
